import { avatarForName, isMcAvatarUrl } from "../embed/avatar";
import type { UserInfo } from "../types";
import { ensureUserId } from "./follow";

const STORAGE_KEY = "magic_comment_user_identity";

/**
 * A random, non-cryptographic fallback id used when crypto.randomUUID is
 * unavailable (old browsers) — good enough to distinguish users in presence.
 */
function randomId(): string {
  return `u_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

/**
 * Return the browser-stable user id, generating and persisting one via
 * localStorage. Guards against SSR / missing storage / missing crypto by
 * falling back to a random (non-persisted) id — never throws.
 */
function stableUserId(): string {
  try {
    const storage = {
      getItem: (key: string) => localStorage.getItem(key),
      setItem: (key: string, value: string) => localStorage.setItem(key, value),
    };
    const generate = () =>
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : randomId();
    return ensureUserId(storage, generate);
  } catch {
    return randomId();
  }
}

/** Predefined colors for user identification */
const USER_COLORS = [
  "#ef4444", // red
  "#f97316", // orange
  "#eab308", // yellow
  "#22c55e", // green
  "#06b6d4", // cyan
  "#3b82f6", // blue
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#14b8a6", // teal
  "#f59e0b", // amber
];

/**
 * Pick a deterministic color from the user's name
 */
function colorFromName(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return USER_COLORS[Math.abs(hash) % USER_COLORS.length]!;
}

/**
 * Generate a random anonymous name
 */
function generateAnonName(): string {
  const adjectives = [
    "Swift",
    "Bold",
    "Bright",
    "Calm",
    "Keen",
    "Quick",
    "Sharp",
    "Warm",
  ];
  const nouns = ["Fox", "Owl", "Bear", "Deer", "Hare", "Wolf", "Hawk", "Lynx"];
  const adj = adjectives[Math.floor(Math.random() * adjectives.length)]!;
  const noun = nouns[Math.floor(Math.random() * nouns.length)]!;
  return `${adj} ${noun}`;
}

/**
 * Load user identity from browser storage.
 * Returns null if no identity exists yet.
 */
export function loadUserIdentity(): UserInfo | null {
  if (typeof window === "undefined") return null;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as UserInfo;
    if (parsed.name && parsed.color) {
      // Backfill identities stored before stable ids existed.
      let changed = false;
      if (!parsed.id) {
        parsed.id = stableUserId();
        changed = true;
      }
      if (!isMcAvatarUrl(parsed.avatarUrl)) {
        parsed.avatarUrl = avatarForName(parsed.name);
        changed = true;
      }
      if (changed) saveUserIdentity(parsed);
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Save user identity to browser storage.
 */
export function saveUserIdentity(user: UserInfo): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Create a user identity from a name string.
 * Assigns a deterministic color based on the name.
 */
export function createUserFromName(name: string, avatarUrl?: string): UserInfo {
  return {
    id: stableUserId(),
    name,
    color: colorFromName(name),
    avatarUrl: isMcAvatarUrl(avatarUrl) ? avatarUrl : undefined,
  };
}

/**
 * Create a default anonymous user identity.
 */
export function createAnonymousUser(avatarUrl?: string): UserInfo {
  const name = generateAnonName();
  return {
    id: stableUserId(),
    name,
    color: colorFromName(name),
    avatarUrl: isMcAvatarUrl(avatarUrl) ? avatarUrl : undefined,
  };
}

/**
 * Get or prompt for user identity.
 * Returns the stored identity, or creates a new anonymous one.
 */
export function getOrCreateUserIdentity(): UserInfo {
  const existing = loadUserIdentity();
  if (existing) return existing;

  const user = createAnonymousUser();
  saveUserIdentity(user);
  return user;
}
